/**
 * Clamps a value between a minimum and maximum
 *
 * @param value - The value to clamp
 * @param min - The minimum value
 * @param max - The maximum value
 * @returns The clamped value
 *
 * @example
 * ```typescript
 * clamp(5, 0, 10); // 5
 * clamp(-5, 0, 10); // 0
 * clamp(15, 0, 10); // 10
 * ```
 */
const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

/**
 * Calculates a percentage from a numerator and denominator
 * Returns 0 if denominator is 0 or invalid
 *
 * @param numerator - The numerator value
 * @param denominator - The denominator value
 * @returns The percentage (0-100)
 *
 * @example
 * ```typescript
 * calculatePercentage(30, 120); // 25
 * calculatePercentage(0, 120); // 0
 * calculatePercentage(30, 0); // 0
 * ```
 */
const calculatePercentage = (numerator: number, denominator: number): number =>
  denominator > 0 ? (numerator / denominator) * 100 : 0;

/**
 * Calculates a value from a percentage and total
 *
 * @param percentage - The percentage (0-100)
 * @param total - The total value
 * @returns The calculated value
 *
 * @example
 * ```typescript
 * calculateFromPercentage(25, 120); // 30
 * calculateFromPercentage(50, 200); // 100
 * ```
 */
const calculateFromPercentage = (percentage: number, total: number): number =>
  (percentage / 100) * total;

export { clamp, calculatePercentage, calculateFromPercentage };
